import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  FileStatus,
  InspectFileState,
  MultipleFileStatus,
  PendingReorderData,
  ReadyFilesQueueItem,
  PendingIdUpdate,
  FailedUploadNotification,
} from 'types';
import { FileErrors } from '@verify/shared-utils';
import { DocumentType, DocumentTypes } from 'types/inspection';
import { initialSingleFileState, initialFilesState } from './filesState';
import { PendingFile, PendingFileStatus } from 'store/request/files/types';

type FileAddPayload = {
  documentType: DocumentType;
  fileState: InspectFileState;
  isNewFile: boolean;
};

type RequestKeyPayload = {
  documentType: DocumentType;
  requestKey: string;
};

type FileRemovePayload = {
  documentType: DocumentType;
};

type MultipleFileStatusPayload = {
  documentType: DocumentType;
  status: MultipleFileStatus;
};

type OCRStatusPayload = {
  documentType: DocumentType;
  status: FileStatus | '';
};

type ShapesJobStatusPayload = FileStatus | '';

const files = createSlice({
  name: 'files',
  initialState: initialFilesState,
  reducers: {
    setFileFromLibrary: (state, action: PayloadAction<FileAddPayload>) => {
      state[action.payload.documentType].file = action.payload.fileState;
    },
    setFile: (state, action: PayloadAction<FileAddPayload>) => {
      state[action.payload.documentType].file = action.payload.fileState;
    },
    removeFile: (state, action: PayloadAction<FileRemovePayload>) => {
      state[action.payload.documentType] = initialSingleFileState;
    },
    setFilesList: (state, action: PayloadAction<Array<InspectFileState>>) => {
      state.filesList = action.payload;
    },
    resetStore: () => {
      return initialFilesState;
    },
    setIsFetchingInspectionFiles: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        // if I'm fetching new files I need to reset the old ones
        state[DocumentTypes.source] = initialSingleFileState;
        state[DocumentTypes.target] = initialSingleFileState;
      }
      state.isFetchingInspectionFiles = action.payload;
    },
    setFetchedFile: (state, action: PayloadAction<FileAddPayload>) => {
      state[action.payload.documentType].file = action.payload.fileState;
    },
    setFileUnavailable: (state, action: PayloadAction<{ documentType: DocumentType; unavailable: boolean }>) => {
      state[action.payload.documentType].fileUnavailable = action.payload.unavailable;
      if (action.payload.unavailable) {
        state[action.payload.documentType].file.fileId = '';
        state[action.payload.documentType].file.url = '';
      }
    },
    setIsFileErrorOpen: (state, action: PayloadAction<FileErrors | undefined>) => {
      state.fileErrorType = action.payload;
    },
    setRequestKey: (state, action: PayloadAction<RequestKeyPayload>) => {
      state.requestKeys[action.payload.documentType] = action.payload.requestKey;
    },
    setMultipleFileStatus: (state, action: PayloadAction<MultipleFileStatusPayload>) => {
      state.multipleFileStatus[action.payload.documentType] = action.payload.status;
    },
    setOCRStatus: (state, action: PayloadAction<OCRStatusPayload>) => {
      state.ocrStatus[action.payload.documentType] = action.payload.status;
    },
    setOCRHasRun: (state, action: PayloadAction<boolean>) => {
      state.ocrHasRun = action.payload;
    },
    setFileCreationCancelled: (state, action: PayloadAction<boolean>) => {
      state.isCreationCancelled = action.payload;
    },
    transferSourceToTarget: (state) => {
      state[DocumentTypes.target].file = state[DocumentTypes.source].file;
      state[DocumentTypes.source] = initialSingleFileState;
    },
    setShapesJobStatus: (state, action: PayloadAction<ShapesJobStatusPayload>) => {
      state.shapesJobStatus = action.payload;
    },
    setFileStatus: (state, action: PayloadAction<{ documentType: DocumentType; status: FileStatus }>) => {
      state[action.payload.documentType].file.status = action.payload.status;
    },
    setActiveFileTransferId: (state, action: PayloadAction<{ documentType: DocumentTypes; id: string | null }>) => {
      state.activeFileTransferIds[action.payload.documentType] = action.payload.id;
    },
    // Pending reorder data actions for immediate modal display
    setPendingReorderData: (state, action: PayloadAction<PendingReorderData>) => {
      state.pendingReorderData = action.payload;
    },
    updatePendingFile: (
      state,
      action: PayloadAction<{ fileId: string; status: PendingFileStatus; progress?: number }>,
    ) => {
      if (state.pendingReorderData) {
        const fileIndex = state.pendingReorderData.pendingFiles.findIndex((f) => f.id === action.payload.fileId);
        if (fileIndex !== -1) {
          state.pendingReorderData.pendingFiles[fileIndex].status = action.payload.status;
          if (action.payload.progress !== undefined) {
            state.pendingReorderData.pendingFiles[fileIndex].progress = action.payload.progress;
          }
        }
      }
    },
    updatePendingFileId: (state, action: PayloadAction<{ tempId: string; realId: string }>) => {
      if (state.pendingReorderData) {
        const fileIndex = state.pendingReorderData.pendingFiles.findIndex((f) => f.id === action.payload.tempId);
        if (fileIndex !== -1) {
          state.pendingReorderData.pendingFiles[fileIndex].id = action.payload.realId;
        }
      }
    },
    addPendingFiles: (state, action: PayloadAction<PendingFile[]>) => {
      if (state.pendingReorderData) {
        state.pendingReorderData.pendingFiles.push(...action.payload);
      }
    },
    removePendingFile: (state, action: PayloadAction<string>) => {
      if (state.pendingReorderData) {
        state.pendingReorderData.pendingFiles = state.pendingReorderData.pendingFiles.filter(
          (f) => f.id !== action.payload,
        );
      }
    },
    clearPendingReorderData: (state) => {
      state.pendingReorderData = null;
    },
    // Ready files queue for concurrent fetches
    addReadyFiles: (state, action: PayloadAction<ReadyFilesQueueItem>) => {
      state.readyFilesQueue.push(action.payload);
    },
    clearReadyFilesQueue: (state) => {
      state.readyFilesQueue = [];
    },
    shiftReadyFilesQueue: (state) => {
      state.readyFilesQueue.shift();
    },
    // Pending ID updates queue
    addPendingIdUpdates: (state, action: PayloadAction<PendingIdUpdate[]>) => {
      state.pendingIdUpdates.push(...action.payload);
    },
    clearPendingIdUpdates: (state) => {
      state.pendingIdUpdates = [];
    },
    // Failed uploads queue
    addFailedUpload: (state, action: PayloadAction<FailedUploadNotification>) => {
      state.failedUploads.push(action.payload);
    },
    clearFailedUploads: (state) => {
      state.failedUploads = [];
    },
    // Track cancelled file IDs
    addCancelledFileId: (state, action: PayloadAction<string>) => {
      if (!state.cancelledFileIds.includes(action.payload)) {
        state.cancelledFileIds.push(action.payload);
      }
    },
    removeCancelledFileId: (state, action: PayloadAction<string>) => {
      state.cancelledFileIds = state.cancelledFileIds.filter((id) => id !== action.payload);
    },
    clearCancelledFileIds: (state) => {
      state.cancelledFileIds = [];
    },
    // Track cancelled filenames (for files cancelled before they get real IDs)
    addCancelledFilename: (state, action: PayloadAction<string>) => {
      if (!state.cancelledFilenames.includes(action.payload)) {
        state.cancelledFilenames.push(action.payload);
      }
    },
    clearCancelledFilenames: (state) => {
      state.cancelledFilenames = [];
    },
    removeCancelledFilenames: (state, action: PayloadAction<string[]>) => {
      state.cancelledFilenames = state.cancelledFilenames.filter((name) => !action.payload.includes(name));
    },
    // Track file to batch mapping
    setFileToBatchMap: (state, action: PayloadAction<{ fileIds: string[]; batchKey: string }>) => {
      action.payload.fileIds.forEach((fileId) => {
        state.fileToBatchMap[fileId] = action.payload.batchKey;
      });
    },
    removeFromFileToBatchMap: (state, action: PayloadAction<string[]>) => {
      action.payload.forEach((fileId) => {
        delete state.fileToBatchMap[fileId];
      });
    },
    clearFileToBatchMap: (state) => {
      state.fileToBatchMap = {};
    },
    // Track cancelled batches
    addCancelledBatchKey: (state, action: PayloadAction<string>) => {
      if (!state.cancelledBatchKeys.includes(action.payload)) {
        state.cancelledBatchKeys.push(action.payload);
      }
    },
    clearCancelledBatchKeys: (state) => {
      state.cancelledBatchKeys = [];
    },
  },
});

export default files;
/* eslint-enable no-param-reassign */
